import { formatDate } from "@intelligems/ig-utils/date-time";

import type { BillingCommandFeedback } from "./commandTypes";
import { commitmentEndsTrial } from "./trialCommitment";
import type { BillingSnapshot } from "./types";
import type {
  ApprovalReturnOutcome,
  ShopifyApprovalKind,
} from "./workflowReducer";

// The Shopify push almost always lands while the merchant is still on Shopify's
// domain, so the return path polls instead of waiting for the websocket. The
// cap bounds a wait the merchant is watching a banner through; past it the
// banner drops and the page reads as an ordinary billing page.
export const APPROVAL_RETURN_POLL_INTERVAL_MS = 2_000;
export const APPROVAL_RETURN_POLL_CAP_MS = 30_000;

export type ApprovalReturnArrival =
  | Readonly<{ kind: "ignored" }>
  | Readonly<{
      kind: "armable";
      alreadyApplied: boolean;
      approvalKind: ShopifyApprovalKind;
    }>;

/**
 * What a `charge_id` arrival means for this organization.
 *
 * `charge_id` alone is not evidence of a Blocking Approval return — the legacy
 * install and first-charge flow carries the same param — so the snapshot has to
 * show a modular organization with a Shopify approval in play. Every such
 * arrival arms the wait, including one whose plan intent has already applied:
 * the wait settles on the same snapshot that armed it, so a change that landed
 * before the merchant got back is celebrated on arrival instead of in silence.
 * `alreadyApplied` reports which of the two the merchant saw.
 */
export const readApprovalReturnArrival = (
  snapshot: BillingSnapshot
): ApprovalReturnArrival => {
  const { org, planIntent, shopifyCapApproval } =
    snapshot.subscriptionAccessData;
  if (!org.isModular || shopifyCapApproval === null) {
    return { kind: "ignored" };
  }

  return {
    kind: "armable",
    alreadyApplied:
      planIntent !== null &&
      planIntent.id === shopifyCapApproval.planIntentId &&
      planIntent.state === "applied",
    approvalKind: shopifyCapApproval.kind,
  };
};

/**
 * The outcome the snapshot now proves, or null while the wait continues.
 *
 * Activation is `planIntent.state === "applied"` and nothing else. Shopify's
 * `approved` is consent to be billed, not the applied change, and a completed
 * command can have applied nothing. A trial commitment is the exception: it
 * applies only at trial end, so its approval is the outcome, unless the plan
 * ends the trial, when approval applies it and activation is the outcome.
 */
export const readApprovalReturnOutcome = (
  snapshot: BillingSnapshot
): ApprovalReturnOutcome | null => {
  const { planCommitment, planIntent, shopifyCapApproval } =
    snapshot.subscriptionAccessData;

  if (
    planIntent !== null &&
    planIntent.state === "applied" &&
    (shopifyCapApproval === null ||
      planIntent.id === shopifyCapApproval.planIntentId)
  ) {
    return "activated";
  }

  if (
    planCommitment?.providerApproval === "approved" &&
    !commitmentEndsTrial(planCommitment) &&
    (shopifyCapApproval === null ||
      (shopifyCapApproval.status === "approved" &&
        planCommitment.planIntentId === shopifyCapApproval.planIntentId))
  ) {
    return "committed";
  }

  if (shopifyCapApproval?.status === "declined") {
    return "declined";
  }
  if (shopifyCapApproval?.status === "expired") {
    return "expired";
  }

  return null;
};

/**
 * What the merchant is told once the wait settles. A merchant who declined or
 * let the approval lapse is owed an explanation; a merchant whose wait ran out,
 * or whose plan intent was cancelled underneath them, is owed silence rather
 * than a message about a change they no longer expect.
 */
export const approvalReturnFeedback = (
  outcome: ApprovalReturnOutcome,
  snapshot: BillingSnapshot
): BillingCommandFeedback | null => {
  const { planCommitment } = snapshot.subscriptionAccessData;
  const trialDeclined =
    planCommitment && planCommitment.providerApproval !== "approved"
      ? {
          message: `Nothing changed. Your free trial continues until ${formatDate(planCommitment.startsAt)}. Choose plan when you're ready.`,
          tone: "info" as const,
          confetti: false,
        }
      : null;

  switch (outcome) {
    case "activated":
      return {
        message: "Plan changes saved",
        tone: "success",
        confetti: true,
      };
    case "committed":
      return planCommitment
        ? {
            message: `Your plan is set. It starts ${formatDate(planCommitment.startsAt)}`,
            tone: "success",
            confetti: true,
          }
        : null;
    case "declined":
      return (
        trialDeclined ?? {
          message: "Your plan is unchanged — the Shopify charge was declined.",
          tone: "info",
          confetti: false,
        }
      );
    case "expired":
      return (
        trialDeclined ?? {
          message:
            "Your plan is unchanged — the Shopify approval request expired.",
          tone: "info",
          confetti: false,
        }
      );
    case "timed_out":
      return null;
  }
};

export type ChargeIdMarker = Readonly<{
  chargeId: string;
  hash: string;
  search: string;
}>;

const stripped = (params: URLSearchParams): string => {
  params.delete("charge_id");
  const rest = params.toString();
  return rest ? `?${rest}` : "";
};

/**
 * Finds Shopify's `charge_id` and returns the location with it removed, or null
 * when the marker is absent.
 *
 * The return URL carries a fragment (`/settings#billing`), and how Shopify
 * appends its param to one is not ours to control: it can arrive either in the
 * query (`/settings?charge_id=1#billing`) or inside the fragment
 * (`/settings#billing?charge_id=1`). The second placement also breaks the
 * Settings page's scroll-to-section, which resolves the whole fragment as an
 * element id, so the marker is stripped out of whichever half carried it and a
 * clean `#billing` is left behind.
 */
export const readChargeIdMarker = ({
  hash,
  search,
}: Readonly<{ hash: string; search: string }>): ChargeIdMarker | null => {
  const searchParams = new URLSearchParams(search);
  if (searchParams.has("charge_id")) {
    return {
      chargeId: searchParams.get("charge_id") ?? "",
      hash,
      search: stripped(searchParams),
    };
  }

  const queryStart = hash.indexOf("?");
  if (queryStart === -1) {
    return null;
  }

  const hashParams = new URLSearchParams(hash.slice(queryStart));
  if (!hashParams.has("charge_id")) {
    return null;
  }

  return {
    chargeId: hashParams.get("charge_id") ?? "",
    hash: `${hash.slice(0, queryStart)}${stripped(hashParams)}`,
    search,
  };
};
